import { useToasts } from "../../components/toasts/useToasts";
import { useAccountState } from "../../redux/reducers/accountReducer";
import { HTTPStatusCode, useApi } from "../api/useApi";
import { Invoice, UserAccount } from "../types";
import { useCaseInvoiceState } from "../../redux/reducers/caseInvoiceReducer";
import { useCaseInvoiceActions } from "../../redux/actions/caseInvoiceActions";

interface InvoiceUpsertResult {
    statusCode: number;
    message?: string;
    invoice?: Invoice;
}

export interface InvoiceEmailResult {
    recipientContactId?: string;
    recipientName?: string;
    message?: string;
}

export const InvoiceApiRoutes = {
    getInvoice: (accountId: string, caseId: string, invoiceId: string) => `account/${accountId}/case/${caseId}/invoice/${invoiceId}`,
    getInvoices: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}`,
    getAccountInvoices: (accountId: string) => `account/${accountId}`,
    upsertInvoice: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/invoice`,
    markInvoicePaid: (accountId: string, caseId: string, invoiceId: string) => `account/${accountId}/case/${caseId}/invoice/${invoiceId}/paid`,
    markInvoicesPaid: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/invoices/paid`,
    emailInvoice: (accountId: string, caseId: string, invoiceId: string) => `account/${accountId}/case/${caseId}/invoice/${invoiceId}/email`,
    deleteInvoice: (accountId: string, caseId: string, invoiceId: string) => `account/${accountId}/case/${caseId}/invoice/${invoiceId}`,
    deleteInvoices: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/invoices`,
}

export type InvoiceService = {
    invoices: Invoice[];
    getInvoiceAsync: (invoiceId: string) => Promise<Invoice | undefined>;
    upsertInvoiceAsync: (invoice: Invoice, sendToast?: boolean) => Promise<void>;
    markInvoicePaidAsync: (invoiceId: string) => Promise<void>;
    markInvoicesPaidAsync: (invoiceIds: string[]) => Promise<void>;
    emailInvoiceAsync: (invoiceId: string, recipientContactId: string, pdfBlob: Blob, fileName: string) => Promise<InvoiceEmailResult>;
    deleteInvoiceAsync: (invoiceId: string) => Promise<void>;
    deleteInvoicesAsync: (invoiceIds: string[], sendToast?: boolean) => Promise<void>;
    loadInvoices: () => Promise<void>;
    loadAccountInvoices: () => Promise<void>;
}
/**
 * This service will load invoices for a specific case and provides methods to interact with them
 * @param caseId 
 * @param autoload
 * @returns
 */
export const useInvoiceService = (caseId?: string): InvoiceService => {

    //Hooks
    const { primaryAccount } = useAccountState();
    const { account } = primaryAccount as UserAccount ?? {};
    const invoiceState = useCaseInvoiceState();
    const invoiceActions = useCaseInvoiceActions();
    const toasts = useToasts();

    //Api
    const api = useApi<Invoice>('api/Invoice');

    //Data Calls
    const getInvoiceAsync = async (invoiceId: string) => {
        let invoice = invoiceState.invoices.find(i => i.invoiceId === invoiceId);
        if (!invoice) {
            invoice = await api.getAsync(InvoiceApiRoutes.getInvoice(account?.accountId as string, caseId as string, invoiceId));
            if (invoice) {
                invoiceActions.addOrReplaceCaseInvoice(invoice);
            }
        }
        return invoice;
    }
    const loadInvoices = async () => {
        const invoices = await api.getAsync(InvoiceApiRoutes.getInvoices(account?.accountId as string, caseId as string));
        invoiceActions.setCaseInvoices(invoices);
    }
    const loadAccountInvoices = async () => {
        const invoices = await api.getAsync(InvoiceApiRoutes.getAccountInvoices(account?.accountId as string));
        invoiceActions.setCaseInvoices(invoices);
    }
    const upsertInvoiceAsync = async (invoice: Invoice, sendToast: boolean = false) => {
        invoiceActions.addOrReplaceCaseInvoice(invoice);
        const updatedInvoiceResult: InvoiceUpsertResult = await api.postAsync(
            invoice,
            InvoiceApiRoutes.upsertInvoice(account?.accountId as string, invoice.caseId as string),
        );
        invoiceActions.addOrReplaceCaseInvoice(updatedInvoiceResult.invoice as Invoice);

        if (sendToast) {
            if(updatedInvoiceResult.statusCode == HTTPStatusCode.created) {
                toasts.invoices.notifyInvoiceCreated(updatedInvoiceResult.invoice as Invoice);
            } else if(updatedInvoiceResult.statusCode == HTTPStatusCode.updated) {
                toasts.invoices.notifyInvoiceUpdated(updatedInvoiceResult.invoice as Invoice);
            }
        }
    };
    const markInvoicePaidAsync = async (invoiceId: string) => {
        let invoice = invoiceState.invoices.find(i => i.invoiceId === invoiceId);
        if(invoice) {
            invoice = {...invoice, isPaid: true};
            invoiceActions.addOrReplaceCaseInvoice(invoice);
        }
        await api.putAsync({}, InvoiceApiRoutes.markInvoicePaid(account?.accountId as string, caseId as string, invoiceId));
        toasts.notifySuccess("Invoice Marked Paid", `Invoice #${invoice?.invoiceNumber} was marked paid.`);
    }
    const markInvoicesPaidAsync = async (invoiceIds: string[]) => {
        const updatedInvoices = invoiceState.invoices.filter(i => invoiceIds.includes(i.invoiceId as string)).map(i => ({...i, isPaid: true}));
        updatedInvoices.forEach(i => invoiceActions.addOrReplaceCaseInvoice(i));

        await api.putAsync(invoiceIds, InvoiceApiRoutes.markInvoicesPaid(account?.accountId as string, caseId as string));
        toasts.notifySuccess('Invoices Marked Paid', `${invoiceIds.length} Invoices were marked paid.`);
    }
    const emailInvoiceAsync = async (invoiceId: string, recipientContactId: string, pdfBlob: Blob, fileName: string) => {
        const formData = new FormData();
        formData.append('invoicePdf', pdfBlob, fileName);
        formData.append('recipientContactId', recipientContactId);

        return await api.postFormDataAsync<InvoiceEmailResult>(
            formData,
            InvoiceApiRoutes.emailInvoice(account?.accountId as string, caseId as string, invoiceId),
        );
    }
    const deleteInvoiceAsync = async (invoiceId: string) => {
        invoiceActions.removeCaseInvoice(invoiceId);
        await api.deleteAsync([invoiceId], InvoiceApiRoutes.deleteInvoice(account?.accountId as string, caseId as string, invoiceId));
        toasts.notifySuccess("Invoice Deleted", "Invoice was deleted.");
    }
    const deleteInvoicesAsync = async (invoiceIds: string[], sendToast: boolean = false) => {
        if (invoiceIds.length) {
            invoiceActions.removeCaseInvoices(invoiceIds);
            await api.deleteAsync(invoiceIds, InvoiceApiRoutes.deleteInvoices(account?.accountId as string, caseId as string));

            if(sendToast){
                if (invoiceIds.length > 1) {
                    toasts.invoices.notifyInvoicesDeleted(invoiceIds.length);
                } else {
                    const invoice = invoiceState.invoices.find(i => i.invoiceId === invoiceIds[0]);
                    if (invoice) {
                        toasts.invoices.notifyInvoiceDeleted(invoice);
                    }
                }
            }
        }
    }

    return {
        invoices: invoiceState.invoices || [],
        getInvoiceAsync,
        upsertInvoiceAsync,
        markInvoicePaidAsync,
        markInvoicesPaidAsync,
        emailInvoiceAsync,
        deleteInvoiceAsync,
        deleteInvoicesAsync,
        loadInvoices,
        loadAccountInvoices
    }
}
